/**
 * The Products & payments page (Products.dc.html, ruling D15): one `/products` page with the catalog
 * on top and the payments section below. Pure TypeScript (no Vue, Pinia or router instance) so the
 * routing, gating, money and catalog rules are covered in Vitest's node environment.
 *
 * URL state of the payments section (all optional):
 *   ?payments=received|due|overdue   which list (default received)
 *   ?month=YYYY-MM | all             the month shown (default the current month, or every month when
 *                                    one company is filtered: a company's payment history)
 *   ?companyId=<id>                  only that company's payments (the removable company chip)
 *   ?dealId=<id>                     only that deal's payments (the removable deal chip); like a company,
 *                                    it shows every month when none is chosen
 * `#payments` scrolls to the section. The old `/payments` URL redirects here (`paymentsRedirect`),
 * through the router so the global guard keeps `?org=`; `/payments/company/:companyId` goes to the
 * company page's Payments tab instead (`companyPaymentsRedirect`, phase 9).
 */
import type { LocationQuery, LocationQueryRaw, RouteLocationRaw } from 'vue-router'
import type { PricingTier, Product } from '@/types'
import type { ProductStats, ProductStatsRow } from '@/types/products'
import { formatMoney } from '@/lib/money'
import { matchMethod } from '@/components/payments/paymentForm'

export type PaymentsTab = 'received' | 'due' | 'overdue'

const TABS: readonly PaymentsTab[] = ['received', 'due', 'overdue']
const MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/

type QueryValue = LocationQuery[string] | LocationQueryRaw[string] | undefined

/** A query value as one string: the first of a repeated key, '' for null / missing. */
function one(value: QueryValue): string {
  const v = Array.isArray(value) ? value[0] : value
  return typeof v === 'string' ? v : v == null ? '' : String(v)
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** `YYYY-MM` of `date` in the browser's zone. */
export function monthOf(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`
}

// ── routes ────────────────────────────────────────────────────────────────

/**
 * Where `/payments` goes: the payments section of `/products`, Received (with a `companyId` param,
 * the company as a filter; no route sends one since phase 9, when a company's payments became its
 * page's Payments tab: `companyPaymentsRedirect`). Only `org` survives from the old query (its
 * `dateRange` presets are gone; the month control replaced them).
 */
export function paymentsRedirect(to: {
  query: LocationQuery
  params: { companyId?: string | string[] | undefined }
}): RouteLocationRaw {
  const query: Record<string, string> = {}
  const org = one(to.query.org)
  if (org) query.org = org
  query.payments = 'received'
  const companyId = one(to.params.companyId)
  if (companyId) query.companyId = companyId
  return { path: '/products', query, hash: '#payments' }
}

/**
 * Where `/payments/company/:companyId` goes (R11): the company page's Payments tab,
 * `/companies/<id>?tab=payments`, keeping `org` and nothing else of the old query. The page falls
 * back to its Overview for anyone without the tab (readonly, root).
 */
export function companyPaymentsRedirect(to: {
  params: { companyId?: string | string[] | undefined }
  query: LocationQuery
}): RouteLocationRaw {
  const query: Record<string, string> = {}
  const org = one(to.query.org)
  if (org) query.org = org
  query.tab = 'payments'
  return { path: `/companies/${encodeURIComponent(one(to.params.companyId))}`, query }
}

export function paymentsTab(q: LocationQuery | LocationQueryRaw): PaymentsTab {
  const v = one(q.payments)
  return (TABS as readonly string[]).includes(v) ? (v as PaymentsTab) : 'received'
}

/** The `?companyId` filter, '' when none. */
export function companyIdOf(q: LocationQuery | LocationQueryRaw): string {
  return one(q.companyId)
}

/** The deal the section is filtered to (`?dealId`), '' for none. */
export function dealIdOf(q: LocationQuery | LocationQueryRaw): string {
  return one(q.dealId)
}

/** The month the section shows (and the window of Due): a valid `?month`, else the current month. */
export function monthParam(q: LocationQuery | LocationQueryRaw, now: Date = new Date()): string {
  const v = one(q.month)
  return MONTH_RE.test(v) ? v : monthOf(now)
}

/** Received lists every month: `?month=all`, or one company or deal with no month chosen (its history). */
export function allMonths(q: LocationQuery | LocationQueryRaw): boolean {
  const v = one(q.month)
  if (v === 'all') return true
  if (MONTH_RE.test(v)) return false
  return companyIdOf(q) !== '' || dealIdOf(q) !== ''
}

/** `month` moved by `delta` months. */
export function shiftMonth(month: string, delta: number): string {
  const m = MONTH_RE.exec(month)
  if (!m) return month
  const index = Number(m[1]) * 12 + (Number(m[2]) - 1) + delta
  return `${Math.floor(index / 12)}-${pad2((index % 12) + 1)}`
}

/** "October", or "December 2025" outside the current year. */
export function monthHeading(month: string, now: Date = new Date()): string {
  const m = MONTH_RE.exec(month)
  if (!m) return month
  const year = Number(m[1])
  const name = new Date(year, Number(m[2]) - 1, 1).toLocaleDateString('en-US', { month: 'long' })
  return year === now.getFullYear() ? name : `${name} ${year}`
}

/**
 * `q` with the payments keys changed: a string sets one, null removes it, undefined leaves it.
 * Every other key (`org`) is kept; `q` is not changed.
 */
export function withPaymentsQuery(
  q: LocationQuery | LocationQueryRaw,
  patch: { payments?: PaymentsTab | null; month?: string | null; companyId?: string | null; dealId?: string | null },
): LocationQueryRaw {
  const next: LocationQueryRaw = { ...q }
  for (const key of ['payments', 'month', 'companyId', 'dealId'] as const) {
    const value = patch[key]
    if (value === undefined) continue
    if (value === null || value === '') delete next[key]
    else next[key] = value
  }
  return next
}

// ── gates ─────────────────────────────────────────────────────────────────

/**
 * Record payment, Edit payment and every per-product "Collected" figure: admins and KAMs, never the
 * root user (the old Payments nav gate). `POST /payment-history` is KAM and up, so members never get
 * a Record / Add payment button.
 */
export function canSeePayments(ctx: { isAdmin: boolean; isKam: boolean; isRootUser: boolean }): boolean {
  return !ctx.isRootUser && (ctx.isAdmin || ctx.isKam)
}

/**
 * The payments section itself: admins and KAMs, and members read-only (R8): the section now holds
 * the payments on their own deals, contacts and companies. Read-only users and the root user see
 * the catalog only.
 */
export function canViewPayments(ctx: { isAdmin: boolean; isKam: boolean; isMember: boolean; isRootUser: boolean }): boolean {
  return !ctx.isRootUser && (ctx.isAdmin || ctx.isKam || ctx.isMember)
}

/**
 * Whether the payments heading carries its own Record payment: below 1024px (`isDesktop` false),
 * where the page header with Record payment is not drawn, for admins only (and never root:
 * `canSeePayments`), whose floating button is New product. A KAM's floating button already is
 * Record payment, so a KAM never gets it twice. At 1024px and up the header has it.
 */
export function headingRecordPayment(
  ctx: { isAdmin: boolean; isKam: boolean; isRootUser: boolean },
  isDesktop: boolean,
): boolean {
  return !isDesktop && ctx.isAdmin && canSeePayments(ctx)
}

// ── money ─────────────────────────────────────────────────────────────────

/**
 * Amounts per currency, biggest first: payments in two currencies are never added into one figure.
 * A row without a currency counts in `fallback` (the org currency).
 */
export function sumByCurrency(
  rows: readonly { amount: number; currency?: string | null | undefined }[],
  fallback = 'USD',
): { currency: string; amount: number }[] {
  const totals = new Map<string, number>()
  for (const row of rows) {
    if (typeof row.amount !== 'number' || !Number.isFinite(row.amount)) continue
    const code = row.currency || fallback
    totals.set(code, (totals.get(code) ?? 0) + row.amount)
  }
  return [...totals.entries()]
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => b.amount - a.amount || a.currency.localeCompare(b.currency))
}

export type MethodTone = 'bkash' | 'bank' | 'cash' | 'neutral'

/** The method tag's tone (board: bKash pink, bank indigo, cash and the rest warm grey). */
export function methodTone(method: string | null | undefined): MethodTone {
  const key = (method ?? '').trim().toLowerCase().replace(/[_-]+/g, ' ')
  if (key === 'bkash') return 'bkash'
  if (key === 'bank' || key.startsWith('bank ')) return 'bank'
  if (key === 'cash') return 'cash'
  return 'neutral'
}

/** A stored method as the dialog spells it (`bkash` → bKash), with Bank Transfer shortened to Bank. */
export function methodLabel(method: string | null | undefined): string {
  const raw = (method ?? '').trim()
  if (!raw) return ''
  const matched = matchMethod(raw)
  return matched === 'Bank Transfer' ? 'Bank' : matched
}

/** A row of `GET /payment-history/due-summary`. */
export interface DueRow {
  dealId: string
  companyId: string
  companyName: string
  subscriptionPrice: number
  billingInterval: string
  nextBillingDate: string
  /** Whole days since the billing date (negative while still due). */
  daysOverdue: number
  /** `YYYY-MM-01T00:00:00.000Z` of the latest payment's period, null without one. */
  lastPaymentDate: string | null
  lastPaymentAmount: number | null
  /** Not sent by the API yet; shown when present. */
  product?: { name: string; tier: string | null } | null
}

const DAY_MS = 24 * 60 * 60 * 1000

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/**
 * The Overdue / Due column: how long past the billing date (the server's day count, as the old
 * page showed it), or how many calendar days until it.
 */
export function dueTiming(row: Pick<DueRow, 'daysOverdue' | 'nextBillingDate'>, tab: PaymentsTab, now: Date = new Date()): string {
  if (tab === 'overdue') {
    const d = Math.max(0, Math.floor(row.daysOverdue))
    if (d === 0) return 'Today'
    return d === 1 ? '1 day' : `${d} days`
  }
  const next = new Date(row.nextBillingDate)
  if (Number.isNaN(next.getTime())) return ''
  const days = Math.round((startOfDay(next) - startOfDay(now)) / DAY_MS)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  return `in ${days} days`
}

/** "22 Oct" in the browser's zone, "3 Dec 2025" outside this year; '' when unusable. */
export function shortDate(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const text = `${d.getDate()} ${d.toLocaleDateString('en-US', { month: 'short' })}`
  return d.getFullYear() === now.getFullYear() ? text : `${text} ${d.getFullYear()}`
}

/** What a payment was for: "Biddaan Packages · PRO", the product alone without a tier. */
export function productLabel(product: { name: string; tier: string | null } | null | undefined): string {
  if (!product?.name) return ''
  return product.tier ? `${product.name} · ${product.tier}` : product.name
}

/** The billing period of a payment (`year` + "01".."12"): "October", "September 2025". */
export function periodLabel(year: number, month: string, now: Date = new Date()): string {
  const m = /^(0[1-9]|1[0-2])$/.exec(String(month).padStart(2, '0'))
  if (!m || !Number.isInteger(year)) return ''
  return monthHeading(`${year}-${m[1]}`, now)
}

/**
 * A due row's last payment: its period, short ("Sep", "Dec 2025"; the API sends
 * `YYYY-MM-01T00:00:00.000Z`, read as text so a zone west of UTC does not turn it into the month
 * before) and its amount in the org currency.
 */
export function lastPaymentLabel(
  row: Pick<DueRow, 'lastPaymentDate' | 'lastPaymentAmount'>,
  currency: string,
  now: Date = new Date(),
): string {
  const m = /^(\d{4})-(0[1-9]|1[0-2])/.exec(row.lastPaymentDate ?? '')
  if (!m) return 'No payments'
  const year = Number(m[1])
  const short = new Date(year, Number(m[2]) - 1, 1).toLocaleDateString('en-US', { month: 'short' })
  const period = year === now.getFullYear() ? short : `${short} ${year}`
  const amount = row.lastPaymentAmount ? formatMoney(row.lastPaymentAmount, currency) : ''
  return amount ? `${period} · ${amount}` : period
}

/** monthly → Monthly. */
export function intervalLabel(interval: string | null | undefined): string {
  const v = (interval ?? '').trim()
  return v ? v.charAt(0).toUpperCase() + v.slice(1) : ''
}

// ── catalog ───────────────────────────────────────────────────────────────

export type CatalogProduct = Pick<
  Product,
  '_id' | 'name' | 'basePrice' | 'currency' | 'pricingType' | 'status' | 'setupFee' | 'taxRate'
> & {
  sku?: string | undefined
  category?: string | undefined
  billingInterval?: Product['billingInterval'] | undefined
  pricingTiers?: Pick<PricingTier, '_id' | 'name' | 'unitPrice' | 'order' | 'isDefault'>[] | undefined
}

export type ProductStatus = Product['status']

export interface TierBox {
  id: string
  name: string
  price: number
  isDefault: boolean
  /** The tier with the most customers, when any tier has one (ties: the first). */
  mostSold: boolean
  /** Null while the stats are missing. */
  customers: number | null
}

export interface CatalogCard {
  id: string
  name: string
  href: string
  status: ProductStatus
  currency: string
  /** `package`: two or more tiers, a wide card with a box per tier. */
  kind: 'package' | 'single'
  cadence: 'Monthly' | 'Yearly' | 'One-off'
  /** The one tier's price, else the base price; 0 = priced per deal. */
  price: number
  tiers: TierBox[]
  /** Figures from GET /products/stats; null while they are missing. */
  customers: number | null
  openDeals: number | null
  /** Payments this month on deals carrying the product; null when none (or no stats). */
  collected: { amount: number; currency: string } | null
  /** Name, SKU and category in lower case, for the search box. */
  haystack: string
}

function cadenceOf(p: CatalogProduct): CatalogCard['cadence'] {
  if (p.pricingType !== 'recurring') return 'One-off'
  return p.billingInterval === 'yearly' ? 'Yearly' : 'Monthly'
}

function orderedTiers(p: CatalogProduct) {
  return [...(p.pricingTiers ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

function cardOf(p: CatalogProduct, row: ProductStatsRow | undefined): CatalogCard {
  const tiers = orderedTiers(p)
  const kind = tiers.length >= 2 ? 'package' : 'single'
  const tierCustomers = (id: string): number | null => {
    if (!row) return null
    return row.tiers.find((t) => t.tierId === id)?.customers ?? 0
  }
  let boxes: TierBox[] = []
  if (kind === 'package') {
    boxes = tiers.map((t) => ({
      id: t._id,
      name: t.name,
      price: t.unitPrice,
      isDefault: !!t.isDefault,
      mostSold: false,
      customers: tierCustomers(t._id),
    }))
    const top = Math.max(0, ...boxes.map((b) => b.customers ?? 0))
    const winner = top > 0 ? boxes.find((b) => b.customers === top) : undefined
    if (winner) winner.mostSold = true
  }
  const collected = row?.collectedThisMonth
  return {
    id: p._id,
    name: p.name,
    href: `/products/${p._id}`,
    status: p.status,
    currency: p.currency || 'USD',
    kind,
    cadence: cadenceOf(p),
    price: tiers.length === 1 ? tiers[0]!.unitPrice : p.basePrice || 0,
    tiers: boxes,
    customers: row ? row.customers : null,
    openDeals: row ? row.openDeals : null,
    collected: collected && collected.currency && collected.amount ? { amount: collected.amount, currency: collected.currency } : null,
    haystack: [p.name, p.sku, p.category].filter(Boolean).join(' ').toLowerCase(),
  }
}

/** The catalog's cards: packages first, then the rest, each group in the given order. */
export function catalogCards(products: readonly CatalogProduct[], stats: ProductStats | null | undefined): CatalogCard[] {
  const byId = new Map((stats?.products ?? []).map((r) => [r.productId, r]))
  const cards = products.map((p) => cardOf(p, stats ? byId.get(p._id) ?? undefined : undefined))
  // A product the stats do not list (created after they loaded) still reads as zero, not unknown.
  if (stats) {
    for (const card of cards) {
      if (card.openDeals === null) {
        card.customers = 0
        card.openDeals = 0
        card.tiers.forEach((t) => (t.customers = 0))
      }
    }
  }
  return [...cards.filter((c) => c.kind === 'package'), ...cards.filter((c) => c.kind === 'single')]
}

/** "One-off · priced per deal", "Monthly · ৳799", "Monthly · 3 tiers". */
export function priceLine(card: CatalogCard): string {
  if (card.kind === 'package') return `${card.cadence} · ${card.tiers.length} tiers`
  return `${card.cadence} · ${card.price > 0 ? formatMoney(card.price, card.currency) : 'priced per deal'}`
}

export function openDealsLabel(n: number | null): string {
  if (n === null) return ''
  if (n === 0) return 'No open deals'
  return n === 1 ? 'On 1 open deal' : `On ${n} open deals`
}

/** The cards to show: the chosen statuses (none chosen = all) matching the search. */
export function filterCards(
  cards: readonly CatalogCard[],
  opts: { search: string; statuses: readonly ProductStatus[] },
): CatalogCard[] {
  const term = opts.search.trim().toLowerCase()
  return cards.filter(
    (c) => (opts.statuses.length === 0 || opts.statuses.includes(c.status)) && (!term || c.haystack.includes(term)),
  )
}

export function statusCounts(products: readonly Pick<CatalogProduct, 'status'>[]): Record<ProductStatus, number> {
  const counts: Record<ProductStatus, number> = { active: 0, inactive: 0, archived: 0 }
  for (const p of products) if (p.status in counts) counts[p.status]++
  return counts
}

/** The catalog's Export CSV: one row per product, its tiers in one cell. */
export function catalogCsvRows(products: readonly CatalogProduct[]): (string | number)[][] {
  const header = ['Name', 'SKU', 'Category', 'Pricing', 'Base price', 'Currency', 'Setup fee', 'Tax rate %', 'Status', 'Tiers']
  return [
    header,
    ...products.map((p) => [
      p.name,
      p.sku ?? '',
      p.category ?? '',
      cadenceOf(p),
      p.basePrice ?? 0,
      p.currency ?? '',
      p.setupFee ?? 0,
      p.taxRate ?? 0,
      p.status,
      orderedTiers(p)
        .map((t) => `${t.name} ${t.unitPrice}${t.isDefault ? ' (default)' : ''}`)
        .join('; '),
    ]),
  ]
}
